import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Axios interceptor to add Authorization header from localStorage
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-refresh on 401
let isRefreshing = false;
let refreshQueue = [];

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/refresh') && !originalRequest.url?.includes('/auth/login')) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axios(originalRequest);
        });
      }
      originalRequest._retry = true;
      isRefreshing = true;
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        isRefreshing = false;
        return Promise.reject(error);
      }
      try {
        const { data } = await axios.post(`${API}/auth/refresh`, {}, {
          headers: { Authorization: `Bearer ${refreshToken}` }
        });
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        refreshQueue.forEach(p => p.resolve(data.access_token));
        refreshQueue = [];
        originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
        return axios(originalRequest);
      } catch {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        refreshQueue.forEach(p => p.reject(error));
        refreshQueue = [];
        window.location.href = '/login';
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastSyncRef = React.useRef('');

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  // Smart sync: lightweight hash check every 10s, full refresh only on change
  useEffect(() => {
    if (!user) return;

    // Set initial hash immediately
    const initHash = async () => {
      try {
        const { data } = await axios.get(`${API}/auth/sync`);
        lastSyncRef.current = data.hash;
      } catch (e) { console.error('Sync init error:', e); }
    };
    initHash();

    const interval = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/auth/sync`);
        if (data.hash !== lastSyncRef.current) {
          lastSyncRef.current = data.hash;
          checkAuth(true);
        }
      } catch (e) { console.error('Sync poll error:', e); }
    }, 10000);
    return () => clearInterval(interval);
  }, [user]);

  // Refresh on tab focus
  useEffect(() => {
    const handleFocus = () => {
      if (localStorage.getItem('access_token')) {
        checkAuth(true);
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const checkAuth = async (silent = false) => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch (error) {
      if (!silent) {
        setUser(false);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      // Store tokens in localStorage as backup for deployed environments
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
      }
      // Fetch full user data including company status
      const { data: fullUser } = await axios.get(`${API}/auth/me`);
      setUser(fullUser);
      return { success: true, user: fullUser };
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMsg = 'فشل تسجيل الدخول';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e?.msg || '').join(' ');
      }
      return { success: false, error: errorMsg };
    }
  };

  const register = async (formData) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, formData);
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
      }
      // Fetch full user data
      const { data: fullUser } = await axios.get(`${API}/auth/me`);
      setUser(fullUser);
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMsg = 'فشل التسجيل';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e?.msg || '').join(' ');
      }
      return { success: false, error: errorMsg };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
